// Short, punchy "ta-DUM" chime played alongside the post-login splash
// screen, synthesized with the Web Audio API so no binary asset needs to
// ship with the client. Best-effort only — browsers may block audio
// without a direct user gesture, so failures are swallowed rather than
// surfaced, since this is a nice-to-have and must never block login.
export function playLoginChime() {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
      return;
    }

    const ctx = new AudioContextClass();
    const now = ctx.currentTime;

    // A compressor lets individual voices run "hot" (loud, layered chords)
    // without harsh digital clipping - this is what makes it sound bigger
    // rather than just distorted.
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.setValueAtTime(-14, now);
    compressor.knee.setValueAtTime(8, now);
    compressor.ratio.setValueAtTime(10, now);
    compressor.attack.setValueAtTime(0.002, now);
    compressor.release.setValueAtTime(0.3, now);
    compressor.connect(ctx.destination);

    // A short feedback delay gives the chord a bit of cinematic "tail"
    // instead of cutting off dead, without needing an impulse-response file.
    const delay = ctx.createDelay();
    delay.delayTime.value = 0.16;
    const feedback = ctx.createGain();
    feedback.gain.value = 0.32;
    const wetGain = ctx.createGain();
    wetGain.gain.value = 0.4;
    delay.connect(feedback);
    feedback.connect(delay);
    delay.connect(wetGain);
    wetGain.connect(compressor);

    const connectVoice = node => {
      node.connect(compressor);
      node.connect(delay);
    };

    // Low sub "boom" - a quick downward pitch sweep, like an impact hit.
    const playBoom = (startOffset, peakGain) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";

      const startAt = now + startOffset;
      const endAt = startAt + 0.28;

      osc.frequency.setValueAtTime(120, startAt);
      osc.frequency.exponentialRampToValueAtTime(42, endAt);

      gain.gain.setValueAtTime(0, startAt);
      gain.gain.linearRampToValueAtTime(peakGain, startAt + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, endAt);

      osc.connect(gain);
      connectVoice(gain);

      osc.start(startAt);
      osc.stop(endAt + 0.05);
    };

    // A single chord voice (one oscillator) - used to stack up the "DUM".
    const playChordNote = (freq, type, startOffset, duration, peakGain) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;

      const startAt = now + startOffset;
      const endAt = startAt + duration;

      gain.gain.setValueAtTime(0, startAt);
      gain.gain.linearRampToValueAtTime(peakGain, startAt + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, endAt);

      osc.connect(gain);
      connectVoice(gain);

      osc.start(startAt);
      osc.stop(endAt + 0.05);
    };

    // "Ta" - low impact hit.
    playBoom(0, 0.9);

    // "-DUM" - a full, loud chord (root + fifth + octave) layered across
    // waveforms for brightness, with a long sustained decay.
    const chordStart = 0.2;
    const chordDuration = 1.1;
    playChordNote(98.0, "sine", chordStart, chordDuration, 0.85); // G2 - weight
    playChordNote(196.0, "triangle", chordStart, chordDuration, 0.7); // G3
    playChordNote(293.66, "triangle", chordStart, chordDuration, 0.55); // D4
    playChordNote(392.0, "sawtooth", chordStart, chordDuration, 0.45); // G4 - brightness

    // Tear down the context once everything has finished playing.
    setTimeout(() => ctx.close().catch(() => {}), 2200);

  } catch (e) {
    // Audio is non-essential - never let a playback failure affect login.
  }
}
